import {TmcTicket} from "../types/OptionsTicket";
import {getOrderApiErrorDetails} from "../../../../js/error.utils";
import {TradefloorApiService} from "../../../../js/services/tradefloorApi.service";
import {OrderFormatService} from "../../../../orders/services/format/OrderFormatService";
import {SimpleModalService} from "../../../../types/SimpleModalService";
import {VettingLevel, VettingOutcomes} from "../types/OrderVettingResponse";
import {PlacedOrdersStore} from "../../../../orders/store/placed/PlacedOrdersStore";
import {PortfolioType} from "../../../models/Portfolio";
import {OrderNumber} from "../../../../orders/models/OrderRecord";
import {AccountStore} from "../../../../account/store/AccountStore";
import {translate} from "angular";

export class TradeService {

    public static readonly id = "tradeService";

    /** @ngInject */
    public constructor(private readonly tradefloorApi: TradefloorApiService,
                       private readonly orderFormatService: OrderFormatService,
                       private readonly simpleModalService: SimpleModalService,
                       private readonly $translate: translate.ITranslateService,
                       private readonly growl: Growl,
                       private readonly placedOrdersStore: PlacedOrdersStore,
                       private readonly accountStore: AccountStore) {
    }

    public async placeTrades(portfolioId: number,
                             trades: ReadonlyArray<any>,
                             description: string,
                             tmcOverride = false,
                             parentOrderNumber?: OrderNumber): Promise<Array<any>> {
        let results: any[];
        if (parentOrderNumber === null || parentOrderNumber === undefined) {
            results = await this.tradefloorApi.placeOrder(portfolioId, trades, description, tmcOverride);
        } else {
            results = await this.tradefloorApi.placeChildOrders(portfolioId, trades, description, tmcOverride, parentOrderNumber);
        }
        return this.processOrderResults(results, portfolioId);
    }

    public async placeTmcTrade(portfolioId: number, trade: TmcTicket): Promise<Array<any>> {
        const results = await this.tradefloorApi.placeTmcOrder(portfolioId, trade);
        return this.processOrderResults(results, portfolioId);
    }

    public processOrderResults(results: Array<any>, portfolioId: number) {
        results.forEach(async (entry) => {
            const order = entry.request;
            if (entry.result.status !== 200) {
                const orderDescription = await this.orderFormatService.format(order);
                if (entry.result.status === 412) {
                    const orderVettingResult = entry.result.data;
                    this.processVettingError(orderDescription, orderVettingResult, order);
                }
            } else {
                const orderIdentifier = entry.result.data;
                this.processPlacedOrder(portfolioId, orderIdentifier);
            }
        });

        const orderErrorDescriptions = results.filter((entry) => entry.result.status !== 200)
                                              .filter((entry) => entry.result.status !== 412)
                                              .map((entry) => getOrderApiErrorDetails(entry.result));

        if (orderErrorDescriptions.length > 0) {
            const pl = orderErrorDescriptions.length > 1;
            this.simpleModalService.error(null,
                                          `<pre>${orderErrorDescriptions.join("\r\n")}</pre>`,
                                          "Order Error", "lg");
        }

        return this.findAllocatedOrders(results);
    }

    private async processVettingError(orderDescription: string, vettingResult: VettingOutcomes, order: any) {
        const defaultConfig: GrowlConfig = {
            title: "Errors when placing Trade",
            ttl: -1
        };

        const firstLine = `Unable to place ${orderDescription}`;

        const errorMessages = vettingResult.outcomes.filter((o) => o.level === VettingLevel.ERROR)
                                    .map((outcome) => outcome.message)
                                    .map((message) => this.$translate(message.code, message.parameters));

        const errors = await Promise.all(errorMessages);

        if (errors.length === 0) {
            throw Error("An unexpected error occurred while attempting to place trade");
        } else if (errors.length === 1) {
            const error = errors[0];
            const errorDescription = `<div>${firstLine}</div>
                                      <div>${error}</div>`;
            this.growl.error(errorDescription, defaultConfig);
        } else {
            const errorDescriptions = errors.map((d) => `<li>${d}</li>`).join();
            const message = `<div>${firstLine}</div>
                             <ul>
                                ${errorDescriptions}
                             </ul>`;
            this.growl.error(message, defaultConfig);
        }
    }

    private findAllocatedOrders(results: Array<any>): Array<any> {
        return results.filter((result) => result.result.status === 200)
                      .map((result) => result.request)
                      .map((request) => {
                          return {
                              code: request.code,
                              quantity: request.quantity
                          };
                      });
    }

    /*
     * This is terrible code but currently the only way I can think to access the portfolio type without a complete
     * rewrite of the way we place orders to split out between simulated and live
     */
    private processPlacedOrder(portfolioId: number, result: number) {
        this.accountStore
            .accounts
            .take(1)
            .subscribe((accounts) => {
                const account = accounts.find((a) => a.id === portfolioId);
                if (account == null) {
                    return;
                } else if (account.portfolioType === PortfolioType.BROKER) {
                    this.placedOrdersStore.brokerOrderPlaced(result as OrderNumber);
                } else if (account.portfolioType === PortfolioType.SIMULATED) {
                    this.placedOrdersStore.simulatedOrderPlaced(result);
                }
        });
    }
}
